import { useState } from 'react';
import { Loader2, Plus, Users } from 'lucide-react';
import { toast } from 'sonner@2.0.3';

import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { divisionApi, type DivisionOverview } from '../../shared/api/division.api';

/**
 * Группы отдела: контент-мейкер и его монтажёры. Астана, Алматы.
 *
 * Группа заводится пустой и живёт даже без людей — это план, а не факт.
 * Руководитель сначала решает, что групп будет две, и лишь потом набирает в
 * них народ; обратный порядок (группа появляется, когда в неё кого-то
 * положили) не даёт завести вторую заранее.
 */
export function GroupsTab({
  divisionId, overview, onChanged,
}: {
  divisionId: string;
  overview: DivisionOverview | undefined;
  onChanged: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);

  const groups = overview?.groups ?? [];
  const members = overview?.members ?? [];
  const makers = members.filter((m) => m.role === 'content_maker' || m.role === 'head');

  async function setMaker(groupId: string, contentMakerId: string | null): Promise<void> {
    setBusy(groupId);
    try {
      await divisionApi.updateGroup(groupId, { contentMakerId });
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Не вышло');
    } finally {
      setBusy(null);
    }
  }

  async function remove(groupId: string, name: string): Promise<void> {
    setBusy(groupId);
    try {
      await divisionApi.deleteGroup(groupId);
      toast.success(`Команда «${name}» распущена`);
      onChanged();
    } catch (error) {
      // Отказ здесь осмысленный — «у группы есть задачи», — и его надо
      // показать целиком, а не заменять общим «не удалось».
      toast.error(error instanceof Error ? error.message : 'Не вышло');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <CreateGroupDialog divisionId={divisionId} makers={makers} onCreated={onChanged} />
      </div>

      {groups.length === 0 && (
        <p className="text-muted-foreground">
          Команд пока нет. Команда — это контент-мейкер и его монтажёры: кто снимает.
        </p>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {groups.map((group) => {
          const crew = members.filter((m) => m.groupId === group.id);
          return (
            <section key={group.id} className="flex flex-col gap-3 rounded-xl border p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate font-medium">{group.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {crew.length > 0
                      ? crew.map((m) => m.name ?? m.username).join(', ')
                      : 'пока никого'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy === group.id}
                  onClick={() => remove(group.id, group.name)}
                >
                  {busy === group.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Распустить'}
                </Button>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground">Контент-мейкер</Label>
                <Select
                  value={group.contentMakerId ?? 'none'}
                  onValueChange={(id) => setMaker(group.id, id === 'none' ? null : id)}
                  disabled={busy === group.id}
                >
                  <SelectTrigger className="h-8"><SelectValue placeholder="не назначен" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">не назначен</SelectItem>
                    {makers.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name ?? m.username}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!group.chatId && (
                <p className="text-xs text-muted-foreground">
                  Канал в телеграме не указан — готовые ролики команде не уедут.
                </p>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function CreateGroupDialog({
  divisionId, makers, onCreated,
}: {
  divisionId: string;
  makers: DivisionOverview['members'];
  onCreated: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [maker, setMaker] = useState('none');
  const [sending, setSending] = useState(false);

  async function submit(): Promise<void> {
    if (!name.trim()) return;
    setSending(true);
    try {
      await divisionApi.createGroup(divisionId, {
        name: name.trim(),
        ...(maker === 'none' ? {} : { contentMakerId: maker }),
      });
      setOpen(false);
      setName('');
      setMaker('none');
      onCreated();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Не вышло');
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="h-4 w-4" />Завести команду</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Новая команда</DialogTitle></DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="group-name">Название</Label>
            <Input
              id="group-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') void submit(); }}
              placeholder="Астана"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Контент-мейкер</Label>
            <Select value={maker} onValueChange={setMaker}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">назначу позже</SelectItem>
                {makers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name ?? m.username}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {makers.length === 0 && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Users className="h-3.5 w-3.5" />
                В отделе пока нет контент-мейкеров — назначьте роль на вкладке «Состав».
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={!name.trim() || sending}>
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}
            Завести
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
